import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #deactivateTimer = null;

  /** After a parent-title click, ignore pointerleave so the mega menu stays open. */
  #keepOpenUntilDismiss = false;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, true);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#setupCascadeListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#deactivateTimer);
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown, true);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupCascadeListeners();
    this.#cleanupMutationObserver();
  }

  /**
   * FXT-201: a click on Magasiner / Shop (or any parent with a submenu) must
   * open the mega menu instead of following /collections/all. Cmd/ctrl-click
   * still opens the collection in a new tab.
   *
   * @param {MouseEvent} event
   */
  toggleSubmenu = (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    this.#keepOpenUntilDismiss = true;
    clearTimeout(this.#deactivateTimer);

    const item =
      event.target instanceof HTMLElement && event.target.matches('[ref="menuitem"]')
        ? event.target
        : findMenuItem(event.target);

    if (!item || !this.headerComponent) return;
    if (item === this.#state.activeItem) return;

    this.activate(event);
  };

  /**
   * @param {PointerEvent} event
   */
  #onDocumentPointerDown = (event) => {
    if (!this.#keepOpenUntilDismiss) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.#keepOpenUntilDismiss = false;
    this.#deactivate();
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#deactivateDelayed();
  };

  /**
   * Schedule a deactivation after a short delay, giving the cursor time
   * to reach the submenu panel. Cleared on activate() so switching items
   * feels instant.
   * @param {HTMLElement | null} [item]
   */
  #deactivateDelayed = (item = this.#state.activeItem) => {
    if (this.#keepOpenUntilDismiss) return;
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = setTimeout(() => {
      this.#deactivate(item);
    }, 150);
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    /** @type {HTMLElement | null} */
    activeCascadeParent: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    clearTimeout(this.#deactivateTimer);
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (this.#keepOpenUntilDismiss) return;
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivateDelayed();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    this.#keepOpenUntilDismiss = false;
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }

    this.#resetCascade();
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Set up hover/focus listeners on cascade parent triggers.
   * Only activates when data-mega-menu-layout="cascade".
   */
  #setupCascadeListeners() {
    if (this.dataset.megaMenuLayout !== 'cascade') return;

    this.querySelectorAll('[data-cascade-trigger]').forEach((trigger) => {
      trigger.addEventListener('pointerenter', this.#activateCascadeParent);
      trigger.addEventListener('focus', this.#activateCascadeParent);
    });
    this.querySelectorAll('[data-cascade-img-trigger]').forEach((trigger) => {
      trigger.addEventListener('pointerenter', this.#activateCascadeChildImage);
      trigger.addEventListener('focus', this.#activateCascadeChildImage);
    });
  }

  #cleanupCascadeListeners() {
    this.querySelectorAll('[data-cascade-trigger]').forEach((trigger) => {
      trigger.removeEventListener('pointerenter', this.#activateCascadeParent);
      trigger.removeEventListener('focus', this.#activateCascadeParent);
    });
    this.querySelectorAll('[data-cascade-img-trigger]').forEach((trigger) => {
      trigger.removeEventListener('pointerenter', this.#activateCascadeChildImage);
      trigger.removeEventListener('focus', this.#activateCascadeChildImage);
    });
  }

  /**
   * Activate a 2nd-level parent and show its child panel in column 2.
   * @param {Event} event
   */
  #activateCascadeParent = (event) => {
    const trigger = /** @type {HTMLElement} */ (event.currentTarget);
    const panelId = trigger.dataset.cascadeTrigger;
    const submenuContainer = trigger.closest('.menu-list__submenu');
    if (!submenuContainer || !panelId) return;

    // Deactivate previous cascade parent
    if (this.#state.activeCascadeParent && this.#state.activeCascadeParent !== trigger) {
      this.#deactivateCascadeParent(submenuContainer);
    }

    // Activate new cascade parent
    trigger.ariaExpanded = 'true';
    const panel = submenuContainer.querySelector(`[data-cascade-panel="${panelId}"]`);
    if (panel) {
      panel.classList.add('is-active');
    }
    this.#state.activeCascadeParent = trigger;

    // Clear image column when parent changes — image updates on child hover
    submenuContainer.querySelector('.mega-menu__cascade-images')
      ?.querySelectorAll('[data-cascade-image]')
      .forEach((el) => el.classList.remove('is-active'));

    // Recalculate submenu height since column 2 content changed
    requestAnimationFrame(() => {
      const height = submenuContainer.offsetHeight;
      if (height > 0 && this.headerComponent) {
        this.headerComponent.style.setProperty('--submenu-height', `${height}px`);
        this.#setFullOpenHeaderHeight(height);
      }
    });
  };

  /**
   * Show the category image for the hovered child link in column 3.
   * @param {Event} event
   */
  #activateCascadeChildImage = (event) => {
    const trigger = /** @type {HTMLElement} */ (event.currentTarget);
    const key = trigger.dataset.cascadeImgTrigger;
    const submenuContainer = trigger.closest('.menu-list__submenu');
    if (!submenuContainer || !key) return;

    const imageContainer = submenuContainer.querySelector('.mega-menu__cascade-images');
    if (!imageContainer) return;

    imageContainer.querySelectorAll('[data-cascade-image]').forEach((el) => el.classList.remove('is-active'));
    imageContainer.querySelector(`[data-cascade-image="${key}"]`)?.classList.add('is-active');
  };

  /**
   * Deactivate the current cascade parent and hide its panel.
   * @param {Element} submenuContainer
   */
  #deactivateCascadeParent(submenuContainer) {
    const prev = this.#state.activeCascadeParent;
    if (!prev) return;

    prev.ariaExpanded = 'false';
    const prevPanelId = prev.dataset.cascadeTrigger;
    if (prevPanelId) {
      const prevPanel = submenuContainer.querySelector(`[data-cascade-panel="${prevPanelId}"]`);
      if (prevPanel) {
        prevPanel.classList.remove('is-active');
      }
    }
  }

  /**
   * Reset all cascade state — called when the mega menu closes.
   */
  #resetCascade() {
    if (this.dataset.megaMenuLayout !== 'cascade') return;

    this.#state.activeCascadeParent = null;
    this.querySelectorAll('[data-cascade-panel]').forEach((panel) => {
      panel.classList.remove('is-active');
    });
    this.querySelectorAll('[data-cascade-image]').forEach((el) => {
      el.classList.remove('is-active');
    });
    this.querySelectorAll('[data-cascade-trigger]').forEach((trigger) => {
      /** @type {HTMLElement} */ (trigger).ariaExpanded = 'false';
    });
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  if (element.matches('[ref="menuitem"]')) {
    return /** @type {HTMLElement} */ (element);
  }

  return element.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
